import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  AppBar,
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import { type FormEvent, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { useNotification } from '../../hooks/use-notification';
import { useReloadBlocker } from '../../hooks/use-reload-blocker';
import { colors } from '../../theme/ai-theme';
import { SlideUpTransition } from '../transitions/slide-up-transition';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSubmit: () => void;
  isSubmitting: boolean;
  isValid: boolean;
  /** Label del bottone di conferma. Default: common.save ("Salva"). */
  submitLabel?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
  slots?: {
    /** Azione compatta allineata al titolo, visibile anche nell'header mobile. */
    titleAction?: ReactNode;
    header?: ReactNode;
    actions?: ReactNode;
    /** Azione alternativa al submit (ignorata se `actions` è fornito): tra Annulla e il submit, o sopra il submit su mobile. */
    secondaryAction?: ReactNode;
    footer?: ReactNode;
  };
  onTransitionExited?: () => void;
}

export function FormDialog({
  open,
  onClose,
  title,
  onSubmit,
  isSubmitting,
  isValid,
  submitLabel,
  maxWidth = 'sm',
  children,
  slots,
  onTransitionExited,
}: FormDialogProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { showInfo } = useNotification();
  // ponytail: dialog-open as dirty-proxy — an automatic update reload while a
  // form dialog is open would lose the operator's input; per-field dirty
  // tracking can replace this if it ever proves too coarse.
  useReloadBlocker(open);
  // Collega il titolo alla Dialog: senza aria-labelledby MUI non espone un
  // accessible name → screen reader non annunciano il titolo e gli E2E non
  // possono usare getByRole('dialog', { name }). Migliora accessibilità reale.
  const titleId = useId();

  function handleClose(_event: object, reason?: string) {
    if (isSubmitting) {
      showInfo(t('common.savingInProgress'));
      return;
    }
    if (reason === 'backdropClick' || reason === 'escapeKeyDown' || reason === undefined) {
      onClose();
    }
  }

  function handleFormSubmit(event: FormEvent<HTMLDivElement>) {
    event.preventDefault();
    // The dialog Paper is a <form> rendered through a portal; React still bubbles
    // its submit up the component tree, so without this it reaches an enclosing
    // page <form> (e.g. the survey step form) and advances it. Keep it contained.
    event.stopPropagation();
    // Anche con actions custom il submit nativo (bottone type="submit" nello
    // slot, o Enter in un campo) deve invocare onSubmit: ingoiarlo lascia
    // bottoni submit morti (bug scoperto dal dialog "Nuovo link" ispezione).
    if (!isValid || isSubmitting) {
      return;
    }
    onSubmit();
  }

  const paperFormSlotProps = {
    component: 'form' as const,
    onSubmit: handleFormSubmit,
    noValidate: true,
    // Nessun dialog HACCP è un modulo di pagamento: disattiviamo l'autofill del
    // browser sull'intero form. Senza questo, i form con un campo "data
    // scadenza" accanto a un numero (es. lotto) vengono classificati da Chrome
    // come moduli carta di credito e mostrano l'autofill dei metodi di pagamento.
    autoComplete: 'off',
  };

  const submitButton = (
    <Button
      type="submit"
      variant="contained"
      disabled={!isValid || isSubmitting}
      fullWidth={isMobile}
      sx={{ minHeight: 48 }}
    >
      {isSubmitting ? <CircularProgress size={24} /> : (submitLabel ?? t('common.save'))}
    </Button>
  );

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        aria-labelledby={titleId}
        slots={{ transition: SlideUpTransition }}
        slotProps={{
          transition: { onExited: onTransitionExited },
          paper: paperFormSlotProps,
        }}
      >
        <AppBar position="sticky" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => handleClose({}, undefined)}
              disabled={isSubmitting}
              aria-label={t('common.back')}
              sx={{
                mr: 1,
                minWidth: 48,
                minHeight: 48,
                color: colors.navigation.text,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              id={titleId}
              variant="h6"
              component="div"
              sx={{ flex: 1, fontWeight: 700, color: colors.navigation.text }}
            >
              {title}
            </Typography>
            {/* Su mobile la testata è la AppBar scura: un bottone `color=
                "primary"` (viola su slate) scenderebbe sotto il minimo di
                contrasto. Qui l'azione prende i colori della navigazione. */}
            {slots?.titleAction && (
              <Box
                sx={{
                  '& .MuiButton-root': {
                    color: colors.navigation.text,
                    borderColor: alpha(colors.navigation.text, 0.6),
                    '&:hover': {
                      borderColor: colors.navigation.text,
                      bgcolor: 'rgba(255, 255, 255, 0.12)',
                    },
                  },
                }}
              >
                {slots.titleAction}
              </Box>
            )}
          </Toolbar>
        </AppBar>
        {slots?.header}
        <DialogContent sx={{ flex: 1, overflow: 'auto', pt: 2, pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>{children}</Box>
        </DialogContent>
        {slots?.footer}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {slots?.actions ? (
            slots.actions
          ) : (
            <>
              {slots?.secondaryAction}
              {submitButton}
            </>
          )}
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId}
      slotProps={{
        transition: { onExited: onTransitionExited },
        paper: paperFormSlotProps,
      }}
    >
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2.5 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {slots?.titleAction}
      </DialogTitle>
      {slots?.header}
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>{children}</Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {slots?.actions ? (
          slots.actions
        ) : (
          <>
            <Button
              onClick={() => handleClose({}, undefined)}
              disabled={isSubmitting}
              sx={{ minHeight: 48 }}
            >
              {t('common.cancel')}
            </Button>
            {slots?.secondaryAction}
            {submitButton}
          </>
        )}
      </DialogActions>
      {slots?.footer}
    </Dialog>
  );
}
